/**
 * „Was kann TwoWheels?" — vollständige, öffentliche Funktionsübersicht.
 *
 * Geht intensiv durch ALLE Bereiche der Plattform. Aufbau: klickbare
 * Haupt-Themen (Akkordeon) → beim Aufklappen ausführliche Unterpunkte mit
 * Direkt-Links zur jeweiligen echten Funktion. Persona-Filter (Käufer /
 * Händler / Vermittler / Dienstleister / Admin) blendet relevante Themen ein.
 *
 * Design-Sprache identisch zur KI-Funktionen-Landingpage (dunkel + #FFCC00).
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Button } from "../components/ui/button";
import {
    Search, Sparkles, Megaphone, Users, Wrench, FileText, Calculator,
    Boxes, ClipboardList, Gauge, Building2, ShoppingBag, Handshake,
    Briefcase, LinkIcon, ShieldCheck, Bot, LayoutGrid, ChevronDown,
    ArrowRight, CheckCircle2, ArrowUpRight, PlayCircle,
} from "lucide-react";

const PERSONA_STYLE = {
    "Käufer": "bg-emerald-100 text-emerald-800 border-emerald-200",
    "Händler": "bg-amber-100 text-amber-800 border-amber-200",
    "Vermittler": "bg-violet-100 text-violet-800 border-violet-200",
    "Dienstleister": "bg-blue-100 text-blue-800 border-blue-200",
    "Admin": "bg-red-100 text-red-800 border-red-200",
    "Alle": "bg-gray-100 text-gray-700 border-gray-200",
};

// Vollständige Themenstruktur. `points`: {title, desc, to?} — `to` verlinkt
// optional direkt zur echten Funktion.
const THEMES = [
    {
        id: "marktplatz", icon: Search, personas: ["Käufer"],
        title: "Marktplatz & intelligente Suche",
        tagline: "Motorräder, Roller, Fahrräder & E-Bikes finden — mit KI statt endloser Filter.",
        points: [
            { title: "KI-Suche in eigenen Worten", desc: '„BMW Tourer bis 18.000 € mit Topcase aus Bayern" wird automatisch in präzise Filter übersetzt und passend sortiert.', to: "/search?category=motorcycle" },
            { title: "Persönliche Empfehlungen", desc: "„Für dich“ lernt aus deinen Favoriten, gespeicherten Suchen, Marken-Vorlieben und angesehenen Inseraten." },
            { title: "Gespeicherte Suchen mit Sofort-Alarm", desc: "Passt ein neues Inserat zu deiner Suche, bekommst du sofort eine Push- und E-Mail-Benachrichtigung." },
            { title: "Fahrzeug-Vergleich", desc: "Mehrere Fahrzeuge Seite an Seite vergleichen — Preis, Leistung, Ausstattung auf einen Blick.", to: "/compare" },
            { title: "Auktionen & Gebote", desc: "Fahrzeuge im Auktionsformat ersteigern, mit Live-Geboten und automatischem Ablauf.", to: "/auctions" },
            { title: "Entdecken-Feed", desc: "Kuratierter Stream mit frischen, spannenden Inseraten zum Stöbern.", to: "/discover" },
            { title: "Auto-Übersetzung EN/FR/IT", desc: "Inserate auf Knopfdruck in Englisch, Französisch oder Italienisch — für Käufer aus ganz Europa." },
            { title: "Händler-Bestenliste", desc: "Die aktivsten und bestbewerteten Händler im Ranking.", to: "/leaderboard" },
            { title: "Detailseite mit allem Drum & Dran", desc: "Foto-Galerie, technische Daten, Finanzierungsrechner, Probefahrt-Anfrage und Direktnachricht an den Händler." },
        ],
    },
    {
        id: "inserieren", icon: Sparkles, personas: ["Händler"],
        title: "Inserieren & KI-Verkaufsassistenz",
        tagline: "In Minuten zum verkaufsstarken Inserat — die KI übernimmt die Fleißarbeit.",
        points: [
            { title: "KI-Preisvorschlag", desc: "Marke, Modell, Baujahr & km plus Vergleichsinserate → fairer B2C/B2B-Preis inkl. Spanne und Begründung." },
            { title: "KI-Foto-Analyse & Schadenerkennung", desc: "Bilder werden automatisch beschriftet (Cockpit, linke Seite …) und Beschädigungen erkannt — auch im Bulk." },
            { title: "Auto-Feature-Tagging", desc: 'Aus „ABS, Heizgriffe, Keyless" werden die passenden Ausstattungs-Häkchen automatisch gesetzt.' },
            { title: "Werks-Daten-Lookup", desc: "Hubraum, Leistung, Antrieb & Getriebe werden automatisch befüllt — mit No-Hallucination-Policy bei seltenen Modellen." },
            { title: "Inserat-Qualitäts-Score", desc: "Live-Bewertung 0–100 mit konkreten Tipps und Klick-zum-fehlenden-Feld." },
            { title: "Compliance-Check vor Veröffentlichung", desc: "Unzulässige Garantieversprechen oder problematische Formulierungen werden vorab geblockt oder gewarnt." },
            { title: "SEO-Meta für Google", desc: "Jedes Inserat erhält automatisch optimierte Titel, Beschreibung und Social-Tags für mehr organische Reichweite." },
            { title: "Inserat erstellen", desc: "Geführter, mehrstufiger Editor mit Live-Vorschau.", to: "/create-listing" },
        ],
    },
    {
        id: "crm", icon: Users, personas: ["Händler"],
        title: "CRM, Leads & „Was zu tun ist“",
        tagline: "Alle Kunden, Anfragen und offenen Aufgaben an einem Ort — priorisiert.",
        points: [
            { title: "Zentrale Kundenkartei", desc: "B2B- und B2C-Kunden mit Fahrzeug-Garage, Historie und Dokumenten — organisationsweit über alle Filialen." },
            { title: "Segmentierung & Zielgruppen", desc: "Kunden nach Kriterien filtern und als Zielgruppe für gezielte Ansprache speichern." },
            { title: "E-Mail-Kampagnen", desc: "Batch-Mailings an Segmente versenden — z. B. Saison-Aktionen oder Service-Erinnerungen." },
            { title: "Smart-Inbox „Was zu tun ist“", desc: "Eine priorisierte To-Do-Liste: unbeantwortete Nachrichten, fällige Termine, offene Freigaben, Dispo-Aufgaben u. v. m." },
            { title: "Lead-Management & Hotness-Score", desc: "Eingehende Leads werden nach Conversion-Wahrscheinlichkeit als Hot/Warm/Cold eingestuft.", to: "/dealer/leads" },
            { title: "Anrufer-Erkennung", desc: "Eingehende Telefonnummer wird sofort dem passenden Kunden zugeordnet.", to: "/dealer/anrufe" },
            { title: "Wiedervorlagen & Follow-ups", desc: "Angebots-Nachfassungen mit Erinnerung, damit kein Interessent verloren geht." },
            { title: "Direkt-zu-Rechnung", desc: "Aus der Kundenkarte mit einem Klick eine vorbefüllte Rechnung oder einen Werkstattauftrag starten." },
        ],
    },
    {
        id: "werkstatt", icon: Wrench, personas: ["Händler"],
        title: "Werkstatt & Service",
        tagline: "Vom Auftrag über die Werkstattkopie bis zur Kundenbestätigung — durchgängig digital.",
        points: [
            { title: "Werkstattaufträge", desc: "Aufträge mit Positionen, Mechaniker-Zuweisung, Abstellort, Laufleistung und Kundenauftrag im Wortlaut.", to: "/dealer/workshop-orders" },
            { title: "Sofort speichern · drucken · bestätigen", desc: "Beim Speichern wird der Auftrag verbindlich angelegt, die Werkstattkopie gedruckt und der Kunde automatisch per E-Mail/WhatsApp/SMS bestätigt — inkl. Ihrer AGB & Datenschutzhinweise." },
            { title: "Mobile Auftragserfassung per QR", desc: "Der Mechaniker scannt den QR auf der Werkstattkopie und erfasst Teile & Arbeitszeiten direkt am Smartphone." },
            { title: "Wunschtermin-Überwachung", desc: "Nahende oder überfällige Fertigstellungstermine erscheinen in „Was zu tun ist“ und lösen einen Push an den zuständigen Mechaniker aus." },
            { title: "Termin-Quick-Actions mit Freigabe", desc: "Termin als erledigt markieren oder verschieben (mit Pflicht-Grund). Die Kundeninfo geht erst nach Freigabe durch den Kundendienst raus." },
            { title: "Werkstatt-Kalender & Smart-Slots", desc: "Auslastung im Blick, plus KI-Vorschläge für die besten Termin-Slots.", to: "/dealer/workshop-calendar" },
            { title: "Kostenvoranschlag mit Online-Freigabe", desc: "KVA als PDF, das der Kunde per Link online freigeben kann." },
            { title: "Garantie & Gewährleistung", desc: "Garantieansprüche dokumentieren und abwickeln, inkl. PDF." },
            { title: "Ersatz-/Mietfahrzeug am Auftrag", desc: "Direkt aus dem Werkstattauftrag ein Ersatzfahrzeug reservieren." },
            { title: "Öffentliche Online-Terminbuchung", desc: "Kunden buchen Werkstatttermine selbst über eine öffentliche Seite Ihres Betriebs." },
            { title: "Geteilte Aufträge mit Partnerwerkstätten", desc: "Aufträge an Partnerbetriebe teilen und Rückmeldungen erhalten.", to: "/dealer/workshop-orders/shared-with-me" },
            { title: "Werkstatt-Bewertungen", desc: "Kundenfeedback zum Service sammeln und anzeigen.", to: "/dealer/workshop-reviews" },
        ],
    },
    {
        id: "rechnungen", icon: FileText, personas: ["Händler"],
        title: "Rechnungen & Faktura",
        tagline: "Schnelle, korrekte Rechnungen — kontextsensitiv nach Fahrzeugart.",
        points: [
            { title: "Rechnungserstellung", desc: "Positionen, MwSt-Sätze, Summen — sauber und revisionssicher.", to: "/dealer/invoices" },
            { title: "Schnell-Positionen & Vorlagen", desc: "Konfigurierbare Quick-Positionen und ganze Rechnungs-Vorlagen, gefiltert nach Fahrzeugart (Motorrad/Roller/Fahrrad/E-Bike)." },
            { title: "Ein-Klick aus Kunde/Auftrag", desc: "Rechnung direkt aus Kundenkarte oder Werkstattauftrag vorbefüllen." },
            { title: "Mahnwesen & Zahlungsportal", desc: "Automatische Zahlungserinnerungen mit Online-Zahlungsseite für den Kunden." },
            { title: "PDF-Versand", desc: "Rechnungen als PDF generieren und versenden." },
        ],
    },
    {
        id: "buchhaltung", icon: Calculator, personas: ["Händler"],
        title: "Buchhaltung, Bank & DATEV",
        tagline: "Vom Kontoauszug bis zum Steuerberater — automatisiert abgeglichen.",
        points: [
            { title: "Buchhaltungs-Cockpit", desc: "Übersicht über Belege, Umsätze und offene Posten.", to: "/dealer/buchhaltung" },
            { title: "Bank-Import & Auto-Abgleich", desc: "Kontoumsätze importieren und automatisch den passenden Rechnungen zuordnen.", to: "/dealer/bank-import" },
            { title: "DATEV-Export", desc: "Buchungsdaten DATEV-konform exportieren — inkl. automatisiertem Versand an den Steuerberater." },
            { title: "Boni-Tracking", desc: "Hersteller-/Lieferanten-Boni nachverfolgen." },
        ],
    },
    {
        id: "wms", icon: Boxes, personas: ["Händler"],
        title: "Lager & Logistik (WMS)",
        tagline: "Teile, Fahrzeuge und Saisonlager unter voller Kontrolle.",
        points: [
            { title: "Lagerverwaltung (WMS)", desc: "Ein- und Auslagerung, Lagerplätze, Bestände — mit Scan-Workflows.", to: "/dealer/wms" },
            { title: "Lieferschein-Scan", desc: "Wareneingänge per Foto/Scan erfassen und einbuchen.", to: "/dealer/wms/scan-delivery" },
            { title: "Winterlager", desc: "Saisonale Einlagerung von Kundenfahrzeugen verwalten und abrechnen.", to: "/dealer/winterlager" },
            { title: "Leasingrückgabe", desc: "Rückgabe-Abwicklung inkl. Zustandsdokumentation.", to: "/dealer/leasingrueckgabe" },
            { title: "Fahrzeuginventur (mobil)", desc: "Bestand mobil zählen und abgleichen.", to: "/dealer/fahrzeuginventur" },
            { title: "Bilddatenbank", desc: "Zentraler Bilderpool für Fahrzeuge und Artikel.", to: "/dealer/bilddatenbank" },
        ],
    },
    {
        id: "disposition", icon: ClipboardList, personas: ["Händler"],
        title: "Disposition & Beschaffung",
        tagline: "Standtage, HU-Fristen und Nachschub — proaktiv gesteuert.",
        points: [
            { title: "Disposition mit Multi-Filtern", desc: "Bestand nach Standtagen, HU/TÜV, Status, Standort u. v. m. filtern und steuern.", to: "/dealer/disposition" },
            { title: "HU/TÜV- & Abmelde-To-Dos", desc: "Fällige HU-Termine und Abmeldungen erscheinen als erledigbare Aufgaben in „Was zu tun ist“." },
            { title: "Einkaufs-/Bestellaufträge", desc: "Bestellungen bei Lieferanten erfassen und nachverfolgen.", to: "/dealer/purchase-orders" },
            { title: "KI-gestützte Nachbeschaffung", desc: "Automatisierte Vorschläge, welche Fahrzeuge/Teile nachbeschafft werden sollten." },
            { title: "Ankauf-Anfragen", desc: "Privatverkäufer-Anfragen entgegennehmen und bewerten.", to: "/ankauf" },
            { title: "Trade-In mit Foto-KI", desc: "Käufer lädt Fotos hoch → KI schätzt Zustand und Preis-Range für das Eintauschfahrzeug." },
        ],
    },
    {
        id: "bewertung", icon: Gauge, personas: ["Händler"],
        title: "Fahrzeugbewertung",
        tagline: "Marktgerechte Werte in Sekunden.",
        points: [
            { title: "Bewertungs-Tool", desc: "Marktwert-Ermittlung als Kalkulationsgrundlage für An- und Verkauf.", to: "/dealer/valuation" },
            { title: "DAT-Anbindung", desc: "Eintausch- und Restwerte über die DAT-Schnittstelle (Vorbereitung/Integration)." },
        ],
    },
    {
        id: "team", icon: Building2, personas: ["Händler"],
        title: "Multi-Standort, Team & Intranet",
        tagline: "Hauptsitz und Filialen als eine Organisation — mit klaren Rollen.",
        points: [
            { title: "Hauptsitz/Filiale-Hierarchie", desc: "Mehrere Standorte unter einem Dach, mit organisationsweiter Sichtbarkeit von Kunden und Daten." },
            { title: "Mitarbeiterverwaltung", desc: "Mitarbeiter mit Funktionen (Verkauf, Kundendienst, Mechaniker, Filialleitung) und eigenen Logins.", to: "/dealer/employees" },
            { title: "Aufgaben-Delegation", desc: "Aufgaben org-weit zuweisen, inkl. Eskalation bei Überfälligkeit und Erledigt-Meldung." },
            { title: "Mitarbeiter-Auslastung", desc: "Auslastung und Produktivität der Mitarbeiter im Blick.", to: "/dealer/employee-utilization" },
            { title: "Intranet", desc: "Interne Mitteilungen und Wissen für das gesamte Team.", to: "/intranet" },
            { title: "Org-Cockpit-Analytics", desc: "Kennzahlen über alle Standorte: Umsatztrend, Top-Werkstattpositionen nach Fahrzeugtyp u. v. m." },
        ],
    },
    {
        id: "marketing", icon: Megaphone, personas: ["Händler"],
        title: "Marketing, Shop & Werbemittel",
        tagline: "Sichtbar werden und nebenbei Teile & Zubehör verkaufen.",
        points: [
            { title: "Eigener Online-Shop", desc: "Teile & Zubehör im eigenen Shop verkaufen — mit sicherem Stripe-Checkout.", to: "/dealers" },
            { title: "Werbemittel-Generator", desc: "Aushänge, Preisschilder und QR-Codes für den Verkaufsraum erzeugen.", to: "/dealer/werbemittel" },
            { title: "Fahrzeug-QR-Cards", desc: "QR-Karten am Fahrzeug, die direkt zum Online-Inserat führen." },
            { title: "Händler-Schaufenster", desc: "Öffentliches Händlerprofil mit allen Inseraten und Bewertungen.", to: "/dealers" },
        ],
    },
    {
        id: "vermittler", icon: Handshake, personas: ["Vermittler"],
        title: "Vermittler & Empfehlungen",
        tagline: "Fahrzeuge vermitteln und an Provisionen verdienen.",
        points: [
            { title: "Vermittler-Dashboard", desc: "Überblick über vermittelte Leads, Status und Erfolge.", to: "/vermittler" },
            { title: "Provisions-Übersicht", desc: "Transparente Abrechnung der verdienten Provisionen.", to: "/vermittler/commissions" },
            { title: "Empfehlungs-Landingpages", desc: "Persönliche Empfehlungs-Links mit eigener Landingpage." },
        ],
    },
    {
        id: "dienstleister", icon: Briefcase, personas: ["Dienstleister"],
        title: "Dienstleister, Hersteller & Clubs",
        tagline: "Auch Partner rund ums Zweirad haben ihren Platz.",
        points: [
            { title: "Dienstleister-Registrierung", desc: "Service-Anbieter registrieren sich und erhalten ein eigenes Dashboard.", to: "/register/service-provider" },
            { title: "Dienstleister-Dashboard", desc: "Anfragen und Aufträge verwalten.", to: "/dienstleister/dashboard" },
            { title: "Hersteller / Importeur", desc: "Eigener Zugang für Hersteller und Importeure.", to: "/register/service-provider?branch=hersteller" },
            { title: "Clubs / Vereine", desc: "Vereine und Clubs mit eigenem Bereich.", to: "/register/service-provider?branch=clubs_vereine" },
        ],
    },
    {
        id: "portale", icon: LinkIcon, personas: ["Käufer", "Händler"],
        title: "Kunden-Self-Service-Portale",
        tagline: "Kunden erledigen vieles selbst — per sicherem Link, ohne Login.",
        points: [
            { title: "Terminportal", desc: "Kunden sehen und bestätigen ihre Termine über einen persönlichen Link." },
            { title: "Zulassungs-Status", desc: "Live-Status der Fahrzeug-Zulassung für den Kunden nachvollziehbar." },
            { title: "Belege-Portal", desc: "Rechnungen und Dokumente sicher zum Download bereitstellen." },
            { title: "Zahlungsportal", desc: "Offene Beträge online begleichen — direkt aus der Mahnung." },
            { title: "KVA-Online-Freigabe", desc: "Kostenvoranschläge digital freigeben." },
            { title: "Partner-Bestätigung", desc: "Partnerbetriebe bestätigen geteilte Aufträge per Link." },
        ],
    },
    {
        id: "ki", icon: Bot, personas: ["Käufer", "Händler", "Admin", "Vermittler"],
        title: "KI-Plattform (19+ Tools)",
        tagline: "19+ KI-Tools für Motorrad-Käufer & -Händler — auf einer Plattform. KI zieht sich durch jeden Bereich.",
        points: [
            { title: "Alle KI-Module live testen", desc: "Interaktive Übersicht mit Live-Demos (KI-Suche, Werks-Daten-Lookup) und Impact-Zahlen je Modul.", to: "/ki-features" },
            // — Käufer-KI —
            { title: "Käufer · KI-Suche in eigenen Worten", desc: '„BMW Tourer bis 18.000 € mit Topcase aus Bayern" wird per KI in präzise Filter übersetzt und sortiert.', to: "/search?category=motorcycle" },
            { title: "Käufer · Persönliche Empfehlungen", desc: "Ranking aus gespeicherten Suchen, Favoriten, Marken-Vorlieben und Klick-Verhalten — lernt mit." },
            { title: "Käufer · Saved-Search Push-Alarm", desc: "Bei Treffern deiner KI-Suche sofort Web-Push + E-Mail." },
            { title: "Käufer · Auto-Übersetzung EN/FR/IT", desc: "Inserate on-demand in Englisch, Französisch oder Italienisch — Cross-Border DACH → EU." },
            { title: "Käufer · Inserat-Tracking-Lernen", desc: "Bereits gesehene Inserate fallen aus den Empfehlungen — nie zweimal dasselbe oben." },
            { title: "Käufer · SEO-Meta für Google", desc: "KI-optimierte Titel, Beschreibung und Social-Tags für bessere Klickraten aus Google." },
            // — Händler-KI —
            { title: "Händler · KI-Preisvorschlag", desc: "Marke/Modell/Baujahr/km + Vergleichsinserate → B2C/B2B-Preis inkl. Spanne und Begründung." },
            { title: "Händler · Auto-Feature-Tagging", desc: 'Aus der Beschreibung („ABS, Heizgriffe, Keyless") werden Ausstattungs-Häkchen automatisch gesetzt.' },
            { title: "Händler · KI-Foto-Analyse", desc: "Bilder werden beschriftet (Cockpit, linke Seite …) und Schäden erkannt — auch im Bulk." },
            { title: "Händler · Werks-Daten-Lookup", desc: "Hubraum, Leistung, Antrieb & Getriebe automatisch — No-Hallucination-Policy bei seltenen Modellen." },
            { title: "Händler · Inserat-Qualitäts-Score", desc: "Live-Score 0–100 mit Top-Tipps und Klick-zum-fehlenden-Feld." },
            { title: "Händler · Compliance-Check vor Publish", desc: "Unzulässige Garantieversprechen oder problematische Phrasen werden vorab geblockt/gewarnt." },
            { title: "Händler · KI-Chat-Antworten", desc: "3 deutsche Antwortvarianten (knapp / ausführlich / mit Call-to-Action) je Käufer-Anfrage." },
            { title: "Händler · Trade-In Schnellbewertung", desc: "Kunde lädt 1–6 Fotos → Gemini Vision schätzt Zustand, Preis-Range und Auffälligkeiten." },
            { title: "Händler · Werkstatt-Smart-Slots", desc: "KI matcht Service-Typ + Auslastung + Stammkunden-Wochentag → Top-3 Termin-Vorschläge." },
            { title: "Händler · Markt-Vergleichs-Inserate", desc: "Zu jeder Ankaufanfrage bis zu 12 vergleichbare aktive Inserate als Kalkulations-Grundlage." },
            // — Plattform-/Admin-KI —
            { title: "Admin · Spam-/Fake-Detection", desc: "Heuristisches Scoring (Stock-Fotos, Caps-Lock, Preis-Ausreißer, Konto-Alter) flaggt verdächtige Inserate." },
            { title: "Admin · Reviews-Sentiment & Auto-Flag", desc: "Jede Bewertung wird auf Sentiment und Auffälligkeiten geprüft (Werbung, Beleidigung, Bot-Sprache)." },
            // — Vermittler-KI —
            { title: "Vermittler · Lead-Hotness-Score", desc: "Kontakt-Qualität + Premium-Marke + Recency + historische Conversion → Hot/Warm/Cold." },
        ],
    },
    {
        id: "admin", icon: ShieldCheck, personas: ["Admin"],
        title: "Administration & Plattform-Schutz",
        tagline: "Die Steuerzentrale für einen sauberen, sicheren Marktplatz.",
        points: [
            { title: "Admin-Cockpit", desc: "Zentrale Verwaltung von Händlern, Inseraten und Plattform-Einstellungen.", to: "/admin" },
            { title: "Compliance & Spam-Erkennung", desc: "Verdächtige Inserate werden heuristisch geflaggt und geprüft." },
            { title: "Bewertungs-Moderation", desc: "Reviews werden automatisch auf Sentiment und Auffälligkeiten geprüft." },
            { title: "Tarife, Punkte & Wallet", desc: "Preismodelle, Belohnungspunkte und Guthaben verwalten." },
            { title: "Support-Tickets & Verläufe", desc: "Tickets, SMS-Verlauf, Cron-Status und KI-Assistent-Statistiken im Blick." },
        ],
    },
    {
        id: "konto", icon: ShieldCheck, personas: ["Alle"],
        title: "Konto & Sicherheit",
        tagline: "Moderner, sicherer Zugang — DSGVO-konform.",
        points: [
            { title: "Login & Google-Anmeldung", desc: "Klassischer Login oder schnelle Anmeldung über Google." },
            { title: "Passkeys (WebAuthn)", desc: "Passwortlos und phishing-sicher per Fingerabdruck/Gesicht anmelden.", to: "/passkeys" },
            { title: "E-Mail-Verifizierung & Rollen", desc: "Verifizierte Konten und rollenbasierte Rechte (Käufer, Händler, Vermittler, Dienstleister, Admin)." },
            { title: "Datenschutz", desc: "DSGVO-konforme Verarbeitung, AGB & Datenschutz je Händler hinterlegbar." },
        ],
    },
];

const PERSONA_FILTERS = ["Alle", "Käufer", "Händler", "Vermittler", "Dienstleister", "Admin"];

// Pro Haupt-Thema ein „Direkt ausprobieren"-Ziel. Öffentlich erlebbare
// Funktionen verlinken auf die Live-Funktion, Login-pflichtige auf die
// passende kostenlose Registrierung.
const THEME_CTA = {
    marktplatz: { to: "/search?category=motorcycle", label: "Marktplatz live öffnen" },
    inserieren: { to: "/create-listing", label: "Inserat mit KI erstellen" },
    crm: { to: "/register?role=b2b", label: "Händler-Konto kostenlos starten" },
    werkstatt: { to: "/register?role=b2b", label: "Werkstatt-Modul testen" },
    rechnungen: { to: "/register?role=b2b", label: "Händler-Konto kostenlos starten" },
    buchhaltung: { to: "/register?role=b2b", label: "Händler-Konto kostenlos starten" },
    wms: { to: "/register?role=b2b", label: "Händler-Konto kostenlos starten" },
    disposition: { to: "/register?role=b2b", label: "Händler-Konto kostenlos starten" },
    bewertung: { to: "/register?role=b2b", label: "Händler-Konto kostenlos starten" },
    team: { to: "/register?role=b2b", label: "Händler-Konto kostenlos starten" },
    marketing: { to: "/dealers", label: "Händler-Shops live ansehen" },
    vermittler: { to: "/register?role=vermittler", label: "Als Vermittler starten" },
    dienstleister: { to: "/register/service-provider", label: "Als Dienstleister registrieren" },
    portale: { to: "/register?role=b2b", label: "Händler-Konto kostenlos starten" },
    ki: { to: "/ki-features", label: "KI live ausprobieren" },
    admin: null,
    konto: { to: "/register", label: "Konto kostenlos erstellen" },
};

// Leichtes Quellen-Tag für Analytics: hängt src=was-kann (+ optional theme=<id>)
// an, respektiert vorhandene Query-Parameter. So lässt sich später auswerten,
// welche Themen die meisten Registrierungen/Aufrufe auslösen.
const withSrc = (to, themeId) => {
    const sep = to.includes("?") ? "&" : "?";
    const theme = themeId ? `&theme=${themeId}` : "";
    return `${to}${sep}src=was-kann${theme}`;
};

// Leichtgewichtiges Klick-Tracking der „Direkt ausprobieren"-/CTA-Buttons.
// Nutzt navigator.sendBeacon (übersteht die Navigation), Fallback fetch+keepalive.
// Öffentlicher Endpunkt, zählt nur in Tages-Buckets — für die Conversion-Rate.
const trackCtaClick = (themeId) => {
    try {
        const url = `${process.env.REACT_APP_BACKEND_URL}/api/track/cta-click`;
        const payload = JSON.stringify({ src: "was-kann", theme: themeId || null });
        if (typeof navigator !== "undefined" && navigator.sendBeacon) {
            navigator.sendBeacon(url, new Blob([payload], { type: "application/json" }));
        } else {
            fetch(url, { method: "POST", headers: { "Content-Type": "application/json" }, body: payload, keepalive: true });
        }
    } catch (_e) { /* Tracking ist optional */ }
};

// Leichtgewichtiges Klick-Tracking der „Öffnen"-Unterlinks je Funktionsthema.
// Getrennter Endpunkt (`sublink-click`), damit die Conversion-Rate je Thema
// nicht durch reine Feature-Erkundung verfälscht wird.
const trackSubLink = (themeId, sub, label) => {
    try {
        const url = `${process.env.REACT_APP_BACKEND_URL}/api/track/sublink-click`;
        const payload = JSON.stringify({ theme: themeId || null, sub: sub || null, label: label || null });
        if (typeof navigator !== "undefined" && navigator.sendBeacon) {
            navigator.sendBeacon(url, new Blob([payload], { type: "application/json" }));
        } else {
            fetch(url, { method: "POST", headers: { "Content-Type": "application/json" }, body: payload, keepalive: true });
        }
    } catch (_e) { /* Tracking ist optional */ }
};

export default function WasKannTwoWheelsPage() {
    const navigate = useNavigate();
    const location = useLocation();
    const [filter, setFilter] = useState("Alle");
    const [open, setOpen] = useState({});

    // Deep-Link per Hash (z. B. /was-kann-twowheels#ki aus dem Header-„KI"-Menü):
    // passendes Thema automatisch aufklappen und hinscrollen.
    useEffect(() => {
        const hash = (location.hash || "").replace("#", "");
        if (hash && THEMES.some((t) => t.id === hash)) {
            setFilter("Alle");
            setOpen((o) => ({ ...o, [hash]: true }));
            const tmr = setTimeout(() => {
                const el = document.getElementById(`theme-${hash}`);
                if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
            }, 280);
            return () => clearTimeout(tmr);
        }
    }, [location.hash]);

    useEffect(() => {
        const oldTitle = document.title;
        document.title = "Was kann TwoWheels? — Die komplette Funktionsübersicht";
        const upsert = (name, content) => {
            let m = document.querySelector(`meta[name="${name}"]`);
            if (!m) { m = document.createElement("meta"); m.setAttribute("name", name); document.head.appendChild(m); }
            m.setAttribute("content", content);
            return m;
        };
        const mDesc = upsert("description",
            "Alle Funktionen von TwoWheels auf einen Blick: Marktplatz & KI-Suche, Inserieren, CRM, Werkstatt, Rechnungen, Buchhaltung, Lager (WMS), Disposition, Multi-Standort, Vermittler und mehr.");
        return () => { document.title = oldTitle; mDesc.remove(); };
    }, []);

    const visible = useMemo(
        () => THEMES.filter((t) => filter === "Alle" || t.personas.includes(filter) || t.personas.includes("Alle")),
        [filter]);

    const toggle = (id) => setOpen((o) => ({ ...o, [id]: !o[id] }));
    const expandAll = () => setOpen(Object.fromEntries(visible.map((t) => [t.id, true])));
    const collapseAll = () => setOpen({});

    const totalPoints = THEMES.reduce((n, t) => n + t.points.length, 0);

    return (
        <div className="bg-gray-50 min-h-screen" data-testid="wkt-page">
            {/* Hero */}
            <section className="bg-gradient-to-br from-[#111827] via-[#1F2937] to-[#111827] text-white py-20 px-4 relative overflow-hidden">
                <div className="absolute inset-0 opacity-10" style={{ backgroundImage: "radial-gradient(circle at 70% 40%, #FFCC00 0%, transparent 55%)" }} />
                <div className="max-w-6xl mx-auto relative">
                    <div className="inline-flex items-center gap-1.5 bg-[#FFCC00] text-[#111827] px-3 py-1 rounded-sm text-[10px] font-bold uppercase tracking-widest mb-6">
                        <LayoutGrid className="w-3 h-3" /> Komplette Funktionsübersicht
                    </div>
                    <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black leading-[1.05] tracking-tight max-w-4xl">
                        Was kann TwoWheels?
                    </h1>
                    <p className="text-gray-300 text-lg mt-6 max-w-2xl leading-relaxed">
                        Vom Marktplatz über die komplette Händler-Software bis zur KI-Assistenz: {THEMES.length} Themenbereiche
                        mit über {totalPoints} Funktionen — inkl. <strong className="text-white">19+ KI-Tools</strong> für Käufer &amp; Händler.
                        Exklusiv für Motorräder, Roller, Fahrräder &amp; E-Bikes. Klick auf ein Thema für die ausführliche Erklärung.
                    </p>
                    <div className="flex flex-wrap gap-3 mt-8">
                        <Button onClick={() => { trackCtaClick(null); navigate(withSrc("/register?role=b2b")); }} className="bg-[#FFCC00] text-[#111827] hover:bg-yellow-300 rounded-sm font-bold uppercase tracking-wider text-xs px-5 h-11" data-testid="wkt-cta-dealer">
                            Als Händler starten <ArrowRight className="w-4 h-4 ml-2" />
                        </Button>
                        <Button onClick={() => { trackCtaClick("ki"); navigate(withSrc("/ki-features")); }} variant="outline" className="bg-transparent border-white text-white hover:bg-white hover:text-[#111827] rounded-sm font-bold uppercase tracking-wider text-xs px-5 h-11" data-testid="wkt-cta-ki">
                            <Sparkles className="w-4 h-4 mr-2" /> KI-Funktionen ansehen
                        </Button>
                    </div>
                </div>
            </section>

            {/* Steuerleiste: Persona-Filter + Alle auf/zu */}
            <section className="max-w-6xl mx-auto px-4 pt-10">
                <div className="flex items-end justify-between gap-3 flex-wrap mb-6">
                    <div>
                        <div className="text-xs font-bold uppercase tracking-widest text-amber-600 mb-2">Filtern nach Rolle</div>
                        <div className="flex flex-wrap gap-2" data-testid="wkt-persona-filter">
                            {PERSONA_FILTERS.map((p) => (
                                <button
                                    key={p}
                                    onClick={() => setFilter(p)}
                                    className={`px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider rounded-sm transition-colors ${filter === p ? "bg-[#111827] text-white" : "bg-white border border-gray-200 text-gray-700 hover:border-[#111827]"}`}
                                    data-testid={`wkt-filter-${p}`}
                                >
                                    {p}
                                </button>
                            ))}
                        </div>
                    </div>
                    <div className="flex gap-2">
                        <button onClick={expandAll} className="text-[11px] font-bold uppercase tracking-wider px-3 py-1.5 rounded-sm border border-gray-300 bg-white hover:border-[#111827]" data-testid="wkt-expand-all">Alle aufklappen</button>
                        <button onClick={collapseAll} className="text-[11px] font-bold uppercase tracking-wider px-3 py-1.5 rounded-sm border border-gray-300 bg-white hover:border-[#111827]" data-testid="wkt-collapse-all">Alle zuklappen</button>
                    </div>
                </div>
            </section>

            {/* Akkordeon der Hauptthemen */}
            <section className="max-w-6xl mx-auto px-4 pb-16">
                <div className="space-y-3" data-testid="wkt-accordion">
                    {visible.map((theme) => {
                        const Icon = theme.icon;
                        const isOpen = !!open[theme.id];
                        return (
                            <div key={theme.id} id={`theme-${theme.id}`} className="bg-white border-2 border-gray-200 rounded-sm overflow-hidden scroll-mt-24" data-testid={`wkt-theme-${theme.id}`}>
                                <button
                                    type="button"
                                    onClick={() => toggle(theme.id)}
                                    aria-expanded={isOpen}
                                    className="w-full text-left p-5 flex items-start gap-4 hover:bg-gray-50 transition-colors"
                                    data-testid={`wkt-theme-toggle-${theme.id}`}
                                >
                                    <div className="w-11 h-11 bg-[#FFCC00] rounded-sm flex items-center justify-center shrink-0">
                                        <Icon className="w-5 h-5 text-[#111827]" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <h2 className="font-display font-black text-lg leading-tight text-[#111827]">{theme.title}</h2>
                                            <span className="text-[10px] font-bold text-gray-400">· {theme.points.length} Funktionen</span>
                                        </div>
                                        <p className="text-sm text-gray-600 mt-1 leading-snug">{theme.tagline}</p>
                                        <div className="flex flex-wrap gap-1 mt-2">
                                            {theme.personas.map((p) => (
                                                <span key={p} className={`text-[9px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-sm border ${PERSONA_STYLE[p] || PERSONA_STYLE.Alle}`}>{p}</span>
                                            ))}
                                        </div>
                                    </div>
                                    <ChevronDown className={`w-5 h-5 text-gray-400 shrink-0 mt-1 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`} />
                                </button>

                                {isOpen && (
                                    <div className="px-5 pb-5 pt-1 border-t border-gray-100" data-testid={`wkt-theme-content-${theme.id}`}>
                                        {THEME_CTA[theme.id] && (
                                            <Link
                                                to={withSrc(THEME_CTA[theme.id].to, theme.id)}
                                                onClick={() => trackCtaClick(theme.id)}
                                                className="inline-flex items-center gap-2 bg-[#111827] hover:bg-black text-[#FFCC00] rounded-sm font-bold uppercase tracking-wider text-[11px] px-4 h-10 mt-4 transition-colors"
                                                data-testid={`wkt-demo-${theme.id}`}
                                            >
                                                <PlayCircle className="w-4 h-4" /> {THEME_CTA[theme.id].label}
                                            </Link>
                                        )}
                                        <ul className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4">
                                            {theme.points.map((pt, i) => (
                                                <li key={i} className="border border-gray-100 bg-gray-50 rounded-sm p-3" data-testid={`wkt-point-${theme.id}-${i}`}>
                                                    <div className="flex items-start gap-2">
                                                        <CheckCircle2 className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
                                                        <div className="min-w-0">
                                                            <div className="font-bold text-[13px] text-[#111827] leading-tight">{pt.title}</div>
                                                            <p className="text-xs text-gray-600 leading-relaxed mt-0.5">{pt.desc}</p>
                                                            {pt.to && (
                                                                <Link to={pt.to} onClick={() => trackSubLink(theme.id, pt.to, pt.title)} className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-amber-700 hover:text-amber-900 mt-1.5" data-testid={`wkt-point-link-${theme.id}-${i}`}>
                                                                    Öffnen <ArrowUpRight className="w-3 h-3" />
                                                                </Link>
                                                            )}
                                                        </div>
                                                    </div>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            </section>

            {/* Closing CTA */}
            <section className="bg-[#111827] text-white py-16 px-4">
                <div className="max-w-4xl mx-auto text-center">
                    <h2 className="font-display text-3xl md:text-4xl font-black tracking-tight mb-4">
                        Eine Plattform für den gesamten Zweirad-Betrieb.
                    </h2>
                    <p className="text-gray-300 mb-8 max-w-2xl mx-auto">
                        Marktplatz, CRM, Werkstatt, Faktura, Buchhaltung, Lager und KI — alles greift ineinander.
                        Kostenlos registrieren und sofort loslegen.
                    </p>
                    <div className="flex flex-wrap gap-3 justify-center">
                        <Link to={withSrc("/register?role=b2b")} onClick={() => trackCtaClick(null)} className="inline-flex items-center gap-2 bg-[#FFCC00] text-[#111827] hover:bg-yellow-300 rounded-sm font-bold uppercase tracking-wider text-xs px-5 h-11 leading-[2.7rem]" data-testid="wkt-cta-register">
                            Jetzt als Händler registrieren <ArrowRight className="w-4 h-4" />
                        </Link>
                        <Link to={withSrc("/search?category=motorcycle")} onClick={() => trackCtaClick("marktplatz")} className="inline-flex items-center gap-2 bg-transparent border border-white text-white hover:bg-white hover:text-[#111827] rounded-sm font-bold uppercase tracking-wider text-xs px-5 h-11 leading-[2.7rem]" data-testid="wkt-cta-browse">
                            Marktplatz entdecken
                        </Link>
                    </div>
                </div>
            </section>
        </div>
    );
}
